import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

import componentStyles from './pc-exposed-form.css.lit';
import globalStyles from './pc-exposed-form.lightDom.css.lit';

import '../pc-container/pc-container';
import '../pc-icon/pc-icon';

/**
 * Exposed Form component
 * @element pc-exposed-form
 * @slot - This element has a default slot
 */
@customElement('pc-exposed-form')
export class PCExposedForm extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;
  static styles: CSSResultArray = [componentStyles];

  @property({ type: Boolean, attribute: 'is-expanded' })
  isExpanded = false;

  @property({
    type: Number,
    reflect: true,
    attribute: 'grid-tablet',
  })
  gridTablet: number;

  @property({
    type: Number,
    reflect: true,
    attribute: 'grid-desktop',
  })
  gridDesktop: number;

  @property({
    type: Number,
    reflect: true,
    attribute: 'grid-ultrawide',
  })
  gridUltrawide: number;

  _buttonLabel = 'Filter by';

  _activeFilters = 0;

  displayFilters() {
    this.isExpanded = true;

    // Disable body scrolling.
    const body = document.querySelector('body');

    if (body) {
      body.style.overflow = 'hidden';
    }
  }

  hideFilters() {
    this.isExpanded = false;

    // Enable body scrolling.
    const body = document.querySelector('body');

    if (body) {
      body.style.overflow = 'auto';
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);

    // Create filter button's label
    const [firstFilter, secondFilter, thirdFilter] =
      this.querySelectorAll('pc-filter');

    this._buttonLabel = 'Filter by';

    if (firstFilter) {
      this._buttonLabel += ' ' + (firstFilter.label?.toLowerCase() ?? '')
      ;
    }

    if (secondFilter) {
      if (!thirdFilter) {
        this._buttonLabel += ' and ' + (secondFilter.label?.toLowerCase() ?? '')
        ;
      } else {
        this._buttonLabel +=
          ', ' + (secondFilter.label?.toLowerCase() ?? '') + ', and more';
      }
    }

    // Get active filters
    const activeFiltersElements = this.querySelectorAll('pc-filter .is-active');
    this._activeFilters = activeFiltersElements.length;
  }

  generateGridStyles() {
    //  Create an array to hold condition
    const styles = [];
    if (this.gridTablet) {
      styles.push(
        css`
          --template-columns-tablet: ${this.gridTablet};
        `
      );
    }
    if (this.gridDesktop) {
      styles.push(
        css`
          --template-columns-desktop: ${this.gridDesktop};
        `
      );
    }
    if (this.gridUltrawide) {
      styles.push(
        css`
          --template-columns-ultrawide: ${this.gridUltrawide};
        `
      );
    }

    return styles.length > 0
      ? html`
          <style>
            :host {
              ${styles.join('\n')}
            }
          </style>
        `
      : null;
  }

  render(): TemplateResult {
    return html`
      ${this.generateGridStyles()}
      <div class="exposed-form">
        <pc-container class="inner">
          <slot name="search"></slot>

          <button
            class="button button--show-filters"
            @click=${this.displayFilters}
            aria-label="Toggle filters section"
            aria-expanded=${this.isExpanded ? 'true' : 'false'}
            aria-controls="filters-section"
            aria-haspopup="true"
          >
            <pc-icon name="filter"></pc-icon>
            <span class="button__label">${this._buttonLabel}</span>
            ${this._activeFilters > 0
              ? html`<span class="counter">${this._activeFilters}</span>`
              : null}
          </button>

          <div
            class="filters ${this.isExpanded ? 'is-expanded' : ''}"
            id="filters-section"
          >
            <div class="filters__header">
              <span class="title">
                <pc-icon name="filter"></pc-icon>Filter by
              </span>

              <button
                class="button button--close"
                @click=${this.hideFilters}
                aria-label="Close filters section"
              >
                <pc-icon name="close" size="lg"></pc-icon>
              </button>
            </div>

            <div class="filters__content">
              <slot name="filters"></slot>
            </div>

            <div class="filters__footer" @click=${this.hideFilters}>
              <button class="button button--view-results">
                View results
                <pc-icon name="arrow-right"></pc-icon>
              </button>
            </div>
          </div>

          <slot name="ancillary-filters"></slot>
        </pc-container>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-exposed-form': PCExposedForm;
  }
}
